import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useUsageQuery } from '../hooks/useUsageQuery';
import { useBranding } from '../contexts/BrandingContext';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useTheme } from '../contexts/ThemeContext';
import { clockIn } from '../lib/time';
import { IconButton } from './ui/Button';
import { Badge } from './ui/Badge';
import DataHealthPill from './DataHealthPill';
import { openWhatsNew } from '../lib/whatsNew';
import { useAssistantOptional } from './Assistant/AssistantContext';

/**
 * The app bar.
 *
 * WHAT WENT AND WHY
 * -----------------
 * * **"Real-time insights"** was false — nothing in the app refetched anything.
 *   It is now a "Synced HH:MM" stamp derived from the last successful fetch, and
 *   clicking it is the refresh. Honest, and it costs no backend work.
 * * **"Worker Stopped"** was a Docker container state rendered as a red alarm for
 *   every new tenant, linked to nothing. Replaced by DataHealthPill, which reports
 *   whether calls are arriving and links to the page that can fix it.
 * * **The bare logout icon** sat unlabelled, unconfirmed, next to the most-clicked
 *   button on the page. It is in an account menu now, which also finally shows who
 *   is signed in — the app never displayed that anywhere.
 */
const Header = ({ toggleSidebar, onRefreshData, onLogout }) => {
  const { data: usage } = useUsageQuery();
  const { brandName } = useBranding();
  const { user, tenantName, role, isOwner } = useAuth();
  const { timeZone } = usePreferences();
  const { preference: themePreference, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const isFetching = useIsFetching() > 0;
  const [menuOpen, setMenuOpen] = useState(false);
  const assistant = useAssistantOptional();
  const [syncedAt, setSyncedAt] = useState(() => clockIn(timeZone));

  const usageTitle = usage?.is_limited
    ? `AI minutes: ${usage.balance ?? 0} of ${usage.total_allocated} remaining`
    : '';
  const usageBadge = usage?.is_limited ? (
    <Badge
      tone={
        usage.is_over_limit || usage.percentage_used >= 90
          ? 'bad'
          : usage.percentage_used >= 70
          ? 'warn'
          : 'neutral'
      }
      icon="fa-bolt"
    >
      {usage.is_over_limit ? 'No AI balance' : `${usage.balance ?? 0} min left`}
    </Badge>
  ) : null;

  const handleRefresh = () => {
    setSyncedAt(clockIn(timeZone));
    if (typeof onRefreshData === 'function') onRefreshData();
    else queryClient.invalidateQueries();
  };

  const email = user?.email || '';
  const initial = (email || tenantName || brandName || '?').charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-surface">
      <div className="flex min-w-0 items-center gap-3 px-4 py-2.5">
        <IconButton
          icon="fa-bars"
          label="Open navigation"
          variant="ghost"
          className="lg:hidden"
          onClick={toggleSidebar}
          aria-controls="app-sidebar"
        />

        <div className="min-w-0 flex-1">
          <div className="truncate text-title-sm font-semibold text-ink-900">
            {tenantName || brandName}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden lg:block">
            <DataHealthPill />
          </span>

          {/* Everyone sees the balance; only an owner can open Usage, so for an
              agent the badge is information rather than a link to a locked page. */}
          {usageBadge && isOwner ? (
            <Link to="/settings/usage" className="hidden sm:block" title={usageTitle}>
              {usageBadge}
            </Link>
          ) : usageBadge ? (
            <span className="hidden sm:block" title={usageTitle}>
              {usageBadge}
            </span>
          ) : null}

          {assistant ? (
            <button
              type="button"
              onClick={assistant.toggle}
              aria-expanded={assistant.open}
              title="Assistant (Ctrl/⌘ K)"
              className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-micro font-medium transition-colors duration-fast ${
                assistant.open
                  ? 'border-brand-line bg-brand-tint text-brand'
                  : 'border-line bg-surface text-ink-700 hover:bg-surface-2'
              }`}
            >
              <i className="fas fa-wand-magic-sparkles" aria-hidden="true" />
              <span className="hidden sm:inline">Ask</span>
              <span className="sr-only sm:hidden">Open the assistant</span>
            </button>
          ) : null}

          {/* Freshness, stated. Clicking it refreshes — one control, one meaning. */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-2.5 py-1.5 text-micro font-medium text-ink-700 transition-colors duration-fast hover:bg-surface-2 disabled:opacity-60"
            title={`Last refreshed at ${syncedAt} (${timeZone}). Click to refresh.`}
          >
            <i
              className={`fas fa-arrows-rotate text-ink-500 ${isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            <span className="font-mono">{isFetching ? 'Syncing…' : `Synced ${syncedAt}`}</span>
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label="Account menu"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-tint text-label font-semibold text-brand transition-colors duration-fast hover:bg-surface-3"
            >
              {initial}
            </button>

            {menuOpen ? (
              <>
                <button
                  type="button"
                  aria-hidden="true"
                  tabIndex={-1}
                  className="fixed inset-0 z-40 cursor-default"
                  onClick={() => setMenuOpen(false)}
                />
                <div
                  role="menu"
                  className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-lg border border-line bg-surface shadow-pop"
                >
                  <div className="border-b border-line px-3 py-2.5">
                    <div className="truncate text-label font-medium text-ink-900">
                      {email || 'Signed in'}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-micro text-ink-500">
                      <span className="truncate">{tenantName}</span>
                      {role ? <Badge tone="neutral" size="sm">{role}</Badge> : null}
                    </div>
                  </div>
                  {/* Appearance is a per-VIEWER choice, so it lives here rather
                      than in tenant Preferences, which is shared by everyone. */}
                  <div className="border-b border-line px-3 py-2.5">
                    <div className="mb-1.5 text-micro font-medium uppercase tracking-wide text-ink-500">
                      Appearance
                    </div>
                    <div
                      role="radiogroup"
                      aria-label="Appearance"
                      className="flex rounded-md border border-line bg-surface-2 p-0.5"
                    >
                      {[
                        { id: 'light', label: 'Light', icon: 'fa-sun' },
                        { id: 'dark', label: 'Dark', icon: 'fa-moon' },
                        { id: 'system', label: 'System', icon: 'fa-desktop' },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          role="radio"
                          aria-checked={themePreference === opt.id}
                          onClick={() => setTheme(opt.id)}
                          className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-micro font-medium transition-colors duration-fast ${
                            themePreference === opt.id
                              ? 'bg-surface text-ink-900 shadow-sm'
                              : 'text-ink-500 hover:text-ink-700'
                          }`}
                        >
                          <i className={`fas ${opt.icon}`} aria-hidden="true" />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="p-1">
                    <Link
                      to="/settings/preferences"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 rounded px-3 py-2 text-label text-ink-700 hover:bg-surface-2"
                    >
                      <i className="fas fa-sliders w-4 text-ink-500" aria-hidden="true" />
                      Preferences &amp; timezone
                    </Link>
                    {/* Notifications is owner-only; an agent would land on a locked page. */}
                    {isOwner ? (
                      <Link
                        to="/settings/notifications"
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 rounded px-3 py-2 text-label text-ink-700 hover:bg-surface-2"
                      >
                        <i className="fas fa-bell w-4 text-ink-500" aria-hidden="true" />
                        Notifications
                      </Link>
                    ) : null}
                  </div>
                  <div className="border-t border-line p-1">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        openWhatsNew();
                      }}
                      className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-label text-ink-700 hover:bg-surface-2"
                    >
                      <i className="fas fa-gift w-4 text-ink-500" aria-hidden="true" />
                      What&apos;s new
                    </button>
                  </div>
                  <div className="border-t border-line p-1">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        onLogout?.();
                      }}
                      className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-label text-bad-text hover:bg-bad-tint"
                    >
                      <i className="fas fa-arrow-right-from-bracket w-4" aria-hidden="true" />
                      Sign out
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Below lg the health pill gets its own row rather than being cut off. */}
      <div className="border-t border-line px-4 py-1.5 lg:hidden">
        <DataHealthPill compact />
      </div>
    </header>
  );
};

export default Header;
